import { isNil } from 'lodash'
import { CirculationHeatPumpParameters } from '../../generated/graphql'
import { EnergyType } from '../EnergyFlow'
import {
  createHourlyProvided,
  createHourlyRequirement,
  createSecondaryHourlyFromEnvironment,
  HourlyEnergyFlow,
  PrimaryHourlyEnergyFlow,
} from '../HourlyEnergyFlow'
import {
  MultiTypeHourlyVariables,
  MultiTypeProvider,
} from './common/MultiTypeProvider'

type ValidatedParameters = {
  cop: number
  maxPower: number
}

type CirculationHeatPumpHourlyVariables = MultiTypeHourlyVariables & {
  availableWasteHeat: number
}

/**
 * Converts electricity to heating and cooling with a limited power.
 */
export class CirculationHeatPumpProvider extends MultiTypeProvider<
  CirculationHeatPumpParameters,
  CirculationHeatPumpHourlyVariables
> {
  public readonly providedEnergyTypes = [EnergyType.Heating, EnergyType.Cooling]

  public isConnectedToWasteHeat = false

  private validatedParameters!: ValidatedParameters

  protected initialize(): void {
    const { cop, maxPower } = this.parameters

    if (isNil(maxPower)) {
      throw new Error('Max power must be defined')
    }

    this.validatedParameters = { cop, maxPower }

    this.isConnectedToWasteHeat = this.hourSeries.length > 0
  }

  protected getHourlyVariables(
    hour: number,
    demands: PrimaryHourlyEnergyFlow[],
  ): CirculationHeatPumpHourlyVariables {
    return {
      ...super.getHourlyVariables(hour, demands),
      availableWasteHeat: this.hourSeries[0]?.values[hour] ?? 0,
    }
  }

  protected provideForHour({
    remainingDemands,
    availableWasteHeat,
  }: CirculationHeatPumpHourlyVariables): HourlyEnergyFlow[] {
    const { cop, maxPower } = this.validatedParameters

    const remainingHeatingDemand = remainingDemands.find(
      (demand) => demand.energyType === EnergyType.Heating,
    )
    const remainingCoolingDemand = remainingDemands.find(
      (demand) => demand.energyType === EnergyType.Cooling,
    )

    if (!remainingHeatingDemand) {
      if (!remainingCoolingDemand) {
        return []
      }
      return [
        createHourlyProvided(
          remainingCoolingDemand.demandId,
          remainingCoolingDemand.energyType,
          0,
        ),
        createHourlyRequirement(
          remainingCoolingDemand.demandId,
          EnergyType.Electricity,
          0,
        ),
        createSecondaryHourlyFromEnvironment(
          remainingCoolingDemand.demandId,
          EnergyType.Heating,
          0,
        ),
      ]
    }

    const remainingCoolingDemandValue = remainingCoolingDemand?.value ?? 0
    const availableHeat = remainingCoolingDemandValue + availableWasteHeat
    const heatingFromAvailableHeat = availableHeat + availableHeat / (cop - 1)

    const providedHeating = createHourlyProvided(
      remainingHeatingDemand.demandId,
      remainingHeatingDemand.energyType,
      Math.min(
        maxPower,
        remainingHeatingDemand.value,
        heatingFromAvailableHeat,
      ),
    )

    const heatCollectedFromProducedHeat =
      providedHeating.value - providedHeating.value / cop

    const providedCooling = remainingCoolingDemand
      ? createHourlyProvided(
          remainingCoolingDemand.demandId,
          remainingCoolingDemand.energyType,
          Math.min(heatCollectedFromProducedHeat, remainingCoolingDemand.value),
        )
      : undefined

    const requirement = createHourlyRequirement(
      providedHeating.demandId,
      EnergyType.Electricity,
      providedHeating.value / cop,
    )

    const providedCoolingValue = providedCooling?.value ?? 0
    const fromEnvironment = createSecondaryHourlyFromEnvironment(
      providedHeating.demandId,
      EnergyType.Heating,
      providedHeating.value - providedCoolingValue - requirement.value,
    )

    return providedCooling
      ? [providedHeating, providedCooling, requirement, fromEnvironment]
      : [providedHeating, requirement, fromEnvironment]
  }
}
